import React, { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { LoginForm } from './LoginForm';
import { Button } from './ui/Button';
import { CheckCircle2, MapPin, Award, Play } from 'lucide-react';
import { VideoModal } from './VideoModal';
export function Hero() {
  const {
    scrollY
  } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 200]);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  return <section className="relative w-full pt-40 pb-16 lg:pt-52 lg:pb-24 overflow-hidden" data-id="element-175">
      {/* Image Background */}
      <div className="absolute inset-0 overflow-hidden" data-id="element-176">
        <img src="https://cdn.magicpatterns.com/uploads/gHgy89W3EcSExnvbgaMzow/hero_bg5.jpg" alt="Real estate professional" className="absolute inset-0 w-full h-full object-cover object-center" data-id="element-177" />
      </div>

      {/* Heavy Dark Base Layer - Reduced by 15% (70% to 55%) */}
      <div className="absolute inset-0 bg-gray-950/55" data-id="element-178" />

      {/* Purple Tint Layer - Keep same */}
      <div className="absolute inset-0 bg-brand-purple-900/40 mix-blend-multiply" data-id="element-179" />

      {/* Deep Gradient Vignette - Reduced by 15% (75% to 60%) */}
      <div className="absolute inset-0 bg-gradient-to-br from-brand-purple-950/60 via-transparent to-brand-purple-950/60" data-id="element-180" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-181">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center" data-id="element-182">
          {/* Left Content */}
          <motion.div className="lg:col-span-7 space-y-8 text-center lg:text-left" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-183">
            <div className="space-y-4" data-id="element-184">
              <motion.div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white text-sm font-medium mx-auto lg:mx-0" initial={{
              scale: 0.9,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              delay: 0.2
            }} data-id="element-185">
                <MapPin className="w-4 h-4 mr-1.5" data-id="element-186" />
                Arizona's Premier Real Estate School
              </motion.div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight" data-id="element-187">
                Arizona Real Estate <br className="hidden lg:block" data-id="element-188" />
                <span className="text-brand-orange-300" data-id="element-189">License Renewal</span>
              </h1>

              <p className="text-xl sm:text-2xl text-white font-semibold" data-id="element-190">
                Continuing Education Made Easy & Simple
              </p>

              <p className="text-lg text-white/90 max-w-2xl mx-auto lg:mx-0" data-id="element-191">
                On-Demand, Zoom, and In-Person CE Classes — Built specifically
                for Arizona Agents. Get your credits quickly and conveniently.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start" data-id="element-192">
              <Button size="lg" className="bg-brand-orange-400 hover:bg-brand-orange-500 text-brand-purple-900 font-bold shadow-xl hover:shadow-2xl transition-all border-none" data-id="element-193">
                View On-Demand Classes
              </Button>
              <Button size="lg" className="bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm border-2 border-white/20 font-semibold shadow-xl" data-id="element-194">
                See Monthly Schedule
              </Button>
            </div>

            {/* Video Teaser */}
            <button onClick={() => setIsVideoOpen(true)} className="group inline-flex items-center gap-3 px-5 py-3 bg-white/10 backdrop-blur-md rounded-xl border-2 border-white/20 hover:bg-white/20 hover:border-white/30 transition-all mx-auto lg:mx-0 shadow-lg" data-id="element-195">
              <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center group-hover:scale-110 transition-transform shadow-md" data-id="element-196">
                <Play className="w-5 h-5 text-brand-purple-600 fill-brand-purple-600 ml-0.5" data-id="element-197" />
              </div>
              <div className="text-left" data-id="element-198">
                <div className="text-sm font-semibold text-white" data-id="element-199">
                  Watch a Quick Intro
                </div>
                <div className="text-xs text-white/80" data-id="element-200">
                  How license renewal works with Desert Sage
                </div>
              </div>
            </button>

            {/* Trust Signals */}
            <div className="pt-4 flex flex-wrap justify-center lg:justify-start gap-x-8 gap-y-4 text-sm" data-id="element-201">
              <div className="flex items-center bg-white/10 backdrop-blur-sm px-3 py-2 rounded-lg border border-white/20" data-id="element-202">
                <CheckCircle2 className="w-4 h-4 text-green-300 mr-2" data-id="element-203" />
                <span className="text-white font-medium" data-id="element-204">
                  ADRE School #S06-0002
                </span>
              </div>
              <div className="flex items-center bg-white/10 backdrop-blur-sm px-3 py-2 rounded-lg border border-white/20" data-id="element-205">
                <Award className="w-4 h-4 text-brand-orange-300 mr-2" data-id="element-206" />
                <span className="text-white font-medium" data-id="element-207">REEA Member</span>
              </div>
              <div className="flex items-center bg-white/10 backdrop-blur-sm px-3 py-2 rounded-lg border border-white/20" data-id="element-208">
                <MapPin className="w-4 h-4 text-white mr-2" data-id="element-209" />
                <span className="text-white font-medium" data-id="element-210">
                  Locally Owned & Operated
                </span>
              </div>
            </div>
          </motion.div>

          {/* Right Content - Login Form */}
          <motion.div className="lg:col-span-5 w-full" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-211">
            <LoginForm data-id="element-212" />
          </motion.div>
        </div>
      </div>

      {/* Video Modal */}
      <VideoModal isOpen={isVideoOpen} onClose={() => setIsVideoOpen(false)} videoId="OhJuESLnDqk" title="Desert Sage Seminars — Welcome" data-id="element-213" />
    </section>;
}